import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const emptyItem = {
  cn_no: "", date: "", destination: "", invoice_no: "", lorry_no: "",
  pkg_wgt: "", freight: "0", labour: "0", b_exp: "0", lcl: "0", net_amount: "0",
};
const emptyForm = {
  bill_no: "",
  date: new Date().toISOString().slice(0, 10),
  party_name: "", party_address: "", party_gstin: "",
  tpn_no: "", sac_no: "996791",
  transported_for: "",
  items: [{ ...emptyItem }],
  subtotal: "0",
  cgst_percent: "2.5", sgst_percent: "2.5", igst_percent: "0",
  cgst_amount: "0", sgst_amount: "0", igst_amount: "0",
  taxable_amount: "0", total_amount: "0", grand_total: "0",
  amount_in_words: "", remarks: "Signed CN Enclosed.",
};

const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };

function inWords(num) {
  const a = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const b = ["","","Twenty","Thirty","Fourty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const inr = (x)=> {
    if (x < 20) return a[x];
    if (x < 100) return b[Math.floor(x/10)] + (x%10 ? " " + a[x%10] : "");
    if (x < 1000) return a[Math.floor(x/100)] + " Hundred" + (x%100 ? " " + inr(x%100) : "");
    if (x < 100000) return inr(Math.floor(x/1000)) + " Thousand" + (x%1000 ? " " + inr(x%1000) : "");
    if (x < 10000000) return inr(Math.floor(x/100000)) + " Lakh" + (x%100000 ? " " + inr(x%100000) : "");
    return inr(Math.floor(x/10000000)) + " Crore" + (x%10000000 ? " " + inr(x%10000000) : "");
  };
  const whole = Math.floor(num);
  return whole ? inr(whole) + " rupees only." : "Zero rupees only.";
}

export default function BillForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(emptyForm);
  const [parties, setParties] = useState([]);
  const [consignments, setConsignments] = useState([]);
  const [bills, setBills] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/parties").then(({ data }) => setParties(data)).catch(() => {});
    api.get("/consignments").then(({ data }) => setConsignments(data)).catch(() => {});
    api.get("/bills").then(({ data }) => setBills(data)).catch(() => {});
    if (isEdit) api.get(`/bills/${id}`).then(({ data }) => setForm({ ...emptyForm, ...data }));
    else api.get("/bills/next-number").then(({ data }) => setForm(f => ({ ...f, bill_no: data.bill_no }))).catch(() => {});
  }, [id, isEdit]);

  // CNs already linked to some OTHER bill are hidden to prevent duplicate billing.
  const billedCns = new Set(
    bills.filter(bl => bl.id !== id).flatMap(bl => (bl.items || []).map(it => it.cn_no).filter(Boolean))
  );
  const availableCns = consignments.filter(c => !billedCns.has(c.cn_no));

  // auto-compute per-line net_amount and totals
  useEffect(() => {
    const items = form.items.map(it => ({
      ...it,
      net_amount: (n(it.freight) + n(it.labour) + n(it.b_exp) + n(it.lcl)).toFixed(2),
    }));
    const total = items.reduce((s, i) => s + n(i.net_amount), 0);
    const cgst = total * n(form.cgst_percent) / 100;
    const sgst = total * n(form.sgst_percent) / 100;
    const igst = total * n(form.igst_percent) / 100;
    const grand = total + cgst + sgst + igst;
    setForm(f => ({
      ...f,
      items,
      total_amount: total.toFixed(2),
      taxable_amount: (cgst + sgst + igst > 0 ? total.toFixed(2) : "0"),
      cgst_amount: cgst.toFixed(2),
      sgst_amount: sgst.toFixed(2),
      igst_amount: igst.toFixed(2),
      subtotal: total.toFixed(2),
      grand_total: grand.toFixed(2),
      amount_in_words: inWords(grand),
    }));
    // eslint-disable-next-line
  }, [JSON.stringify(form.items.map(i => `${i.freight}-${i.labour}-${i.b_exp}-${i.lcl}`)), form.cgst_percent, form.sgst_percent, form.igst_percent]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target?.value ?? e }));
  const setItem = (idx, k, v) => setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [k]: v } : it) }));

  // Auto-fill CN details when a valid CN No is entered
  const pickCn = (idx, cnNo) => {
    const c = consignments.find(x => x.cn_no === cnNo);
    if (!c) { setItem(idx, "cn_no", cnNo); return; }
    setForm(f => ({
      ...f,
      items: f.items.map((it, i) => i === idx ? {
        ...it,
        cn_no: c.cn_no,
        date: c.date || it.date,
        destination: c.to_location || it.destination,
        invoice_no: c.inv_no || it.invoice_no,
        lorry_no: c.truck_no || it.lorry_no,
        pkg_wgt: (c.no_pkgs && (c.charged_weight || c.actual_weight))
                  ? `${c.no_pkgs}/${c.charged_weight || c.actual_weight}`
                  : (c.charged_weight || c.actual_weight || it.pkg_wgt),
        freight: (n(it.freight) === 0 && n(c.grand_total) > 0) ? c.grand_total : it.freight,
      } : it),
    }));
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...emptyItem, date: f.date }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickParty = (name) => {
    const p = parties.find(x => x.name === name);
    setForm(f => ({ ...f, party_name: name, party_address: p?.address ?? f.party_address, party_gstin: p?.gstin ?? f.party_gstin }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = isEdit ? await api.put(`/bills/${id}`, form) : await api.post("/bills", form);
      toast.success(isEdit ? "Updated" : `Created ${res.data.bill_no}`);
      navigate(`/portal/bills/${res.data.id}/print`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8" data-testid="bill-form-page">
      <h1 className="font-display text-3xl text-slate-900 mb-6">{isEdit ? "Edit Freight Bill" : "New Freight Bill"}</h1>
      <form onSubmit={onSubmit} className="space-y-6">
        <section className="bg-white p-5 rounded-xl border border-slate-100 grid grid-cols-3 gap-4">
          <div><Label>Bill Number</Label><Input value={form.bill_no} onChange={set("bill_no")} placeholder="396/006/26-27" data-testid="bill-no" /></div>
          <div><Label>Bill Date</Label><Input type="date" value={form.date} onChange={set("date")} required data-testid="bill-date" /></div>
          <div><Label>M/s Party Name</Label>
            <Input list="parties" value={form.party_name} onChange={(e)=>pickParty(e.target.value)} data-testid="bill-party" />
            <datalist id="parties">{parties.map(p=><option key={p.id} value={p.name}/>)}</datalist>
          </div>
          <div><Label>Party GSTIN</Label><Input value={form.party_gstin} onChange={set("party_gstin")}/></div>
          <div className="col-span-3"><Label>Party Address (M/s address)</Label><Textarea value={form.party_address} onChange={set("party_address")} rows={2}/></div>
          <div><Label>TPN #</Label><Input value={form.tpn_no} onChange={set("tpn_no")} placeholder="TAC00217"/></div>
          <div><Label>SAC NO</Label><Input value={form.sac_no} onChange={set("sac_no")}/></div>
          <div><Label>Transported Goods For (comma separated)</Label><Input value={form.transported_for} onChange={set("transported_for")} placeholder="KBODY, NGORI"/></div>
        </section>

        <section className="bg-white p-5 rounded-xl border border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Bill Items</h3>
            <button type="button" onClick={addItem} className="text-sm text-[#0b5cc4] hover:underline inline-flex items-center gap-1" data-testid="add-bill-item"><Plus size={14}/> Add Item</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1200px]">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-1 py-2">Date</th>
                  <th className="text-left px-1 py-2">Consignment (CN No)</th>
                  <th className="text-left px-1 py-2">Destination</th>
                  <th className="text-left px-1 py-2">Invoice No</th>
                  <th className="text-left px-1 py-2">Lorry No</th>
                  <th className="text-left px-1 py-2">Pkg/Wgt</th>
                  <th className="text-right px-1 py-2">Freight</th>
                  <th className="text-right px-1 py-2">Labour</th>
                  <th className="text-right px-1 py-2">B.Exp</th>
                  <th className="text-right px-1 py-2">Detain</th>
                  <th className="text-right px-1 py-2">Net Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-100">
                    <td className="px-1 py-1"><Input type="date" value={it.date} onChange={(e)=>setItem(idx,"date",e.target.value)} className="text-xs" data-testid={`bill-item-date-${idx}`}/></td>
                    <td className="px-1 py-1">
                      <Input list={`cns-${idx}`} value={it.cn_no} onChange={(e)=>pickCn(idx, e.target.value)} className="text-xs" placeholder="Type or pick CN" data-testid={`bill-item-cn-${idx}`}/>
                      <datalist id={`cns-${idx}`}>
                        {availableCns.map(c => <option key={c.id} value={c.cn_no} label={`${c.consignor_name} → ${c.consignee_name}`} />)}
                      </datalist>
                    </td>
                    <td className="px-1 py-1"><Input value={it.destination} onChange={(e)=>setItem(idx,"destination",e.target.value)} className="text-xs" data-testid={`bill-item-destination-${idx}`}/></td>
                    <td className="px-1 py-1"><Input value={it.invoice_no} onChange={(e)=>setItem(idx,"invoice_no",e.target.value)} className="text-xs" data-testid={`bill-item-invoice-${idx}`}/></td>
                    <td className="px-1 py-1"><Input value={it.lorry_no} onChange={(e)=>setItem(idx,"lorry_no",e.target.value)} className="text-xs" data-testid={`bill-item-lorry-${idx}`}/></td>
                    <td className="px-1 py-1"><Input value={it.pkg_wgt} onChange={(e)=>setItem(idx,"pkg_wgt",e.target.value)} placeholder="2/972KG" className="text-xs" data-testid={`bill-item-pkg-wgt-${idx}`}/></td>
                    <td className="px-1 py-1"><Input value={it.freight} onChange={(e)=>setItem(idx,"freight",e.target.value)} className="text-xs text-right" data-testid={`bill-item-freight-${idx}`}/></td>
                    <td className="px-1 py-1"><Input value={it.labour} onChange={(e)=>setItem(idx,"labour",e.target.value)} className="text-xs text-right" data-testid={`bill-item-labour-${idx}`}/></td>
                    <td className="px-1 py-1"><Input value={it.b_exp} onChange={(e)=>setItem(idx,"b_exp",e.target.value)} className="text-xs text-right" data-testid={`bill-item-bexp-${idx}`}/></td>
                    <td className="px-1 py-1"><Input value={it.lcl} onChange={(e)=>setItem(idx,"lcl",e.target.value)} className="text-xs text-right" data-testid={`bill-item-detain-${idx}`}/></td>
                    <td className="px-1 py-1 text-right font-mono">{it.net_amount}</td>
                    <td className="px-1 py-1"><button type="button" onClick={()=>removeItem(idx)} className="p-2 hover:bg-red-50 rounded text-red-600"><Trash2 size={14}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="bg-white p-5 rounded-xl border border-slate-100 grid grid-cols-3 gap-4">
          <div><Label>CGST %</Label><Input value={form.cgst_percent} onChange={set("cgst_percent")}/></div>
          <div><Label>SGST %</Label><Input value={form.sgst_percent} onChange={set("sgst_percent")}/></div>
          <div><Label>IGST %</Label><Input value={form.igst_percent} onChange={set("igst_percent")}/></div>
          <div><Label>Total Amount</Label><Input value={form.total_amount} readOnly/></div>
          <div><Label>Taxable Amount</Label><Input value={form.taxable_amount} readOnly/></div>
          <div><Label>Grand Total (₹)</Label><Input value={form.grand_total} onChange={set("grand_total")} className="font-semibold" data-testid="bill-grand-total"/></div>
          <div className="col-span-3"><Label>Amount in Words (auto)</Label><Textarea value={form.amount_in_words} onChange={set("amount_in_words")} rows={2}/></div>
          <div className="col-span-3"><Label>Remarks</Label><Input value={form.remarks} onChange={set("remarks")}/></div>
        </section>

        <Button type="submit" className="btn-primary" disabled={saving} data-testid="bill-save-btn">
          {saving ? "Saving…" : (isEdit ? "Save Changes" : "Save & Print")}
        </Button>
      </form>
    </div>
  );
}
